import { Component, useRef, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const STAR_COUNT = 550;
const GRID_SIZE = 46;
const GRID_DIVISIONS = 46;
const CELL_SIZE = GRID_SIZE / GRID_DIVISIONS;
const CYAN = "#00F0FF";
const CYAN_DIM = "#0284C7";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
};

const STAR_COLOR_PALETTE = [
  [1, 1, 1],
  [0.878, 0.949, 0.992],
  [0.22, 0.741, 0.973],
  [0, 0.941, 1],
];

function buildStars(count) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 30;
    positions[i * 3 + 1] = Math.random() * 14 - 2;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 26 - 4;
    const c = STAR_COLOR_PALETTE[Math.floor(Math.random() * STAR_COLOR_PALETTE.length)];
    colors[i * 3] = c[0];
    colors[i * 3 + 1] = c[1];
    colors[i * 3 + 2] = c[2];
  }
  return [positions, colors];
}

// Deep-space starfield — white/cyan micro points with a slow twinkle pulse
const Starfield = ({ reducedMotion }) => {
  const groupRef = useRef();
  const matARef = useRef();
  const matBRef = useRef();

  const [posA, colA] = useMemo(() => buildStars(Math.round(STAR_COUNT * 0.6)), []);
  const [posB, colB] = useMemo(() => buildStars(Math.round(STAR_COUNT * 0.4)), []);

  useFrame((state) => {
    if (groupRef.current && !reducedMotion) groupRef.current.rotation.y += 0.00025;
    const t = state.clock.elapsedTime;
    if (matARef.current) matARef.current.opacity = 0.55 + Math.sin(t * 0.6) * 0.25;
    if (matBRef.current) matBRef.current.opacity = 0.55 + Math.sin(t * 0.6 + Math.PI) * 0.25;
  });

  return (
    <group ref={groupRef}>
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={posA.length / 3} array={posA} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={colA.length / 3} array={colA} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          ref={matARef}
          size={0.05}
          vertexColors
          transparent
          opacity={0.7}
          sizeAttenuation
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={posB.length / 3} array={posB} itemSize={3} />
          <bufferAttribute attach="attributes-color" count={colB.length / 3} array={colB} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial
          ref={matBRef}
          size={0.035}
          vertexColors
          transparent
          opacity={0.5}
          sizeAttenuation
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
};

// Infinite forward-scrolling Tron-style neon grid (floor + faint ceiling mirror)
const TronGrid = ({ y, flip, reducedMotion }) => {
  const ref = useRef();
  useFrame((_, delta) => {
    if (!ref.current || reducedMotion) return;
    ref.current.position.z += Math.min(delta, 0.05) * 1.4;
    if (ref.current.position.z >= CELL_SIZE) ref.current.position.z -= CELL_SIZE;
  });
  return (
    <group position={[0, y, 0]} rotation={flip ? [Math.PI, 0, 0] : [0, 0, 0]}>
      <gridHelper ref={ref} args={[GRID_SIZE, GRID_DIVISIONS, CYAN, CYAN_DIM]} />
    </group>
  );
};

// A soft pulsing "engine core" glow near the horizon vanishing point
const HorizonCore = () => {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const s = 1 + Math.sin(state.clock.elapsedTime * 1.1) * 0.12;
    ref.current.scale.set(s, s, s);
  });
  return (
    <group position={[0, 0.3, -18]}>
      <mesh ref={ref}>
        <icosahedronGeometry args={[0.5, 1]} />
        <meshBasicMaterial color={CYAN} transparent opacity={0.35} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
};

// Draws a small rounded-square "brand chip" for a social platform onto an offscreen canvas
function drawRoundedSquare(ctx, size, radius, fill) {
  const r = radius;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(size, 0, size, size, r);
  ctx.arcTo(size, size, 0, size, r);
  ctx.arcTo(0, size, 0, 0, r);
  ctx.arcTo(0, 0, size, 0, r);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
}

const PLATFORM_ICON_SPECS = {
  facebook: { bg: "#1877F2", draw: (ctx, s) => { ctx.fillStyle = "#fff"; ctx.font = `bold ${s * 0.62}px system-ui, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("f", s / 2, s * 0.58); } },
  instagram: {
    bg: null,
    draw: (ctx, s) => {
      const grad = ctx.createLinearGradient(0, s, s, 0);
      grad.addColorStop(0, "#FEDA75"); grad.addColorStop(0.35, "#D62976"); grad.addColorStop(0.7, "#962FBF"); grad.addColorStop(1, "#4F5BD5");
      drawRoundedSquare(ctx, s, s * 0.22, grad);
      ctx.strokeStyle = "#fff"; ctx.lineWidth = s * 0.06;
      ctx.strokeRect(s * 0.26, s * 0.26, s * 0.48, s * 0.48);
      ctx.beginPath(); ctx.arc(s / 2, s / 2, s * 0.14, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(s * 0.68, s * 0.32, s * 0.03, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill();
    },
  },
  youtube: {
    bg: "#FF0033",
    draw: (ctx, s) => {
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.moveTo(s * 0.4, s * 0.3); ctx.lineTo(s * 0.4, s * 0.7); ctx.lineTo(s * 0.72, s * 0.5); ctx.closePath(); ctx.fill();
    },
  },
  tiktok: {
    bg: "#000000",
    draw: (ctx, s) => {
      const cx = s * 0.52, cy = s * 0.42, r = s * 0.13;
      ctx.fillStyle = "#25F4EE"; ctx.beginPath(); ctx.arc(cx - s * 0.05, cy + s * 0.05, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#FE2C55"; ctx.beginPath(); ctx.arc(cx + s * 0.05, cy - s * 0.02, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillRect(cx + r * 0.35, cy - s * 0.3, s * 0.08, s * 0.42);
      ctx.beginPath(); ctx.arc(cx + r * 0.35 + s * 0.04, cy - s * 0.3, s * 0.1, Math.PI, Math.PI * 1.9); ctx.fill();
    },
  },
  x: { bg: "#000000", draw: (ctx, s) => { ctx.fillStyle = "#fff"; ctx.font = `bold ${s * 0.56}px Arial Black, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("X", s / 2, s * 0.56); } },
  linkedin: { bg: "#0A66C2", draw: (ctx, s) => { ctx.fillStyle = "#fff"; ctx.font = `bold ${s * 0.42}px system-ui, sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText("in", s / 2, s * 0.58); } },
};

function makePlatformTexture(platform) {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const spec = PLATFORM_ICON_SPECS[platform];
  if (spec.bg) drawRoundedSquare(ctx, size, size * 0.22, spec.bg);
  spec.draw(ctx, size);
  return new THREE.CanvasTexture(canvas);
}

// Small floating brand chips for each social platform, drifting along the grid toward the camera
const DataPacket = ({ startZ, x, y, platform, speed, texture }) => {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = (state.clock.elapsedTime * speed + startZ) % 1;
    ref.current.position.set(x, y + Math.sin(state.clock.elapsedTime * 0.8 + startZ * 6) * 0.15, -18 + t * 22);
    ref.current.material.opacity = 0.95 - t * 0.75;
    ref.current.material.rotation = Math.sin(state.clock.elapsedTime * 0.3 + startZ * 5) * 0.15;
  });
  return (
    <sprite ref={ref} scale={[0.5, 0.5, 0.5]}>
      <spriteMaterial map={texture} transparent depthWrite={false} />
    </sprite>
  );
};


const PLATFORMS = ["facebook", "instagram", "youtube", "tiktok", "x", "linkedin"];

// Built once and cached for the lifetime of the tab — never regenerated on remount
let _cachedTextures = null;
function getPlatformTextures() {
  if (!_cachedTextures) {
    _cachedTextures = {};
    PLATFORMS.forEach((p) => { _cachedTextures[p] = makePlatformTexture(p); });
  }
  return _cachedTextures;
}

const Scene = ({ reducedMotion }) => {
  const textures = useMemo(() => getPlatformTextures(), []);
  const packets = useMemo(
    () => [
      { x: -3.2, y: -1.2, platform: "facebook", speed: 0.07, startZ: 0 },
      { x: 3.1, y: -0.2, platform: "instagram", speed: 0.055, startZ: 0.35 },
      { x: -1.4, y: 0.8, platform: "youtube", speed: 0.065, startZ: 0.62 },
      { x: 1.7, y: -0.9, platform: "tiktok", speed: 0.06, startZ: 0.15 },
      { x: -2.3, y: 1.6, platform: "x", speed: 0.05, startZ: 0.8 },
      { x: 2.6, y: 0.5, platform: "linkedin", speed: 0.075, startZ: 0.48 },
    ],
    []
  );
  return (
    <>
      <fog attach="fog" args={["#020617", 6, 20]} />
      <ambientLight intensity={0.25} />
      <Starfield reducedMotion={reducedMotion} />
      <TronGrid y={-1.55} reducedMotion={reducedMotion} />
      <TronGrid y={3.1} flip reducedMotion={reducedMotion} />
      <HorizonCore />
      {packets.map((p, i) => (
        <DataPacket key={i} {...p} texture={textures[p.platform]} />
      ))}
    </>
  );
};

function hasWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch {
    return false;
  }
}

class ThreeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const ThreeBackground = () => {
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const [webglOk, setWebglOk] = useState(true);

  useEffect(() => {
    setWebglOk(hasWebGL());
  }, []);

  const fallback = <div data-testid="three-bg-canvas" className="fixed inset-0 -z-10 cyber-grid-fallback" />;

  if (isMobile || !webglOk) {
    return fallback;
  }

  return (
    <ThreeErrorBoundary fallback={fallback}>
      <div className="fixed inset-0 -z-10" data-testid="three-bg-canvas">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 100%, #040D21 0%, #020617 55%, #01040A 100%)" }} />
        <Canvas camera={{ position: [0, 1.3, 5.5], fov: 58 }} dpr={1} gl={{ antialias: false, alpha: true }}>
          <Scene reducedMotion={reducedMotion} />
        </Canvas>
      </div>
    </ThreeErrorBoundary>
  );
};

export default ThreeBackground;
